import { memo, useMemo } from 'react';

import MoreOptionsDropdown from 'domains/core/components/MoreOptionsDropdown';
import useArchiveDisplayCondition, { DisplayConditionListItem } from 'hooks/mutations/useArchiveDisplayCondition';
import useUnarchiveDisplayCondition from 'hooks/mutations/useUnarchiveDisplayCondition';

export type Props = {
    condition: DisplayConditionListItem;
    isArchivedView: boolean;
    onEdit: (condition: DisplayConditionListItem) => void;
};

/**
 * Row menu for the Manage Display Conditions table. Active view: Edit, Archive.
 * Archived view: Unarchive only (editing an archived condition is out of scope).
 */
const ManageDisplayConditionsTableOptionsCell = ({ condition, isArchivedView, onEdit }: Props) => {
    const { mutate: archiveCondition, isLoading: isArchiving } = useArchiveDisplayCondition(condition.id);
    const { mutate: unarchiveCondition, isLoading: isUnarchiving } = useUnarchiveDisplayCondition(condition.id);

    const menuItems = useMemo(() => {
        if (isArchivedView) {
            return [
                {
                    name: 'Unarchive',
                    onClick: () => unarchiveCondition(),
                    disabled: isUnarchiving,
                    testId: `manage-display-condition-unarchive-${condition.id}`,
                },
            ];
        }

        return [
            {
                name: 'Edit',
                onClick: () => onEdit(condition),
                testId: `manage-display-condition-edit-${condition.id}`,
            },
            {
                name: 'Archive',
                onClick: () => archiveCondition(),
                disabled: isArchiving,
                testId: `manage-display-condition-archive-${condition.id}`,
            },
        ];
    }, [isArchivedView, condition, onEdit, archiveCondition, isArchiving, unarchiveCondition, isUnarchiving]);

    return (
        <MoreOptionsDropdown buttonTestId={`manage-display-condition-options-${condition.id}`} menuItems={menuItems} />
    );
};

export default memo(ManageDisplayConditionsTableOptionsCell);
